// Route Factory - generates routes based on configuration
import React, { Suspense } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { getAuthRoutes } from './shared/authRoutes'
import { getCoreRoutes } from './shared/coreRoutes'
import { getCommonRoutes } from './shared/commonRoutes'
import { processRouteConfigs } from './utils/routeHelpers'
import useIntegrateStripeIfNeeded from './hooks/useIntegrateStripeIfNeeded'
import RouteChunkFallback from './shared/RouteChunkFallback'

/**
 * Wrapper component that handles Stripe integration hook
 */
const RoutesWithStripeIntegration = ({ children, isLogged }) => {
	// Hook must be called unconditionally
	useIntegrateStripeIfNeeded(isLogged)
	return children
}

/**
 * Project Routes Component
 * React component that generates routes based on configuration
 * @param {object} props - Component props
 * @param {object} props.config - Project route configuration
 * @param {function} props.config.getProjectRoutes - Function that returns project-specific routes
 * @param {object} props.config.options - Additional options
 * @param {boolean} props.config.options.useSignupToken - Include signup token route (default: false)
 * @param {boolean} props.isLogged - Is user logged in
 * @param {boolean} props.isLoggingIn - Is user currently logging in
 * @returns {JSX.Element} Routes component
 */
export const ProjectRoutes = ({ config, isLogged, isLoggingIn }) => {
	const state = useSelector(state => state)
	const { getProjectRoutes } = config

	// Get project-specific routes
	const projectRoutesConfig = getProjectRoutes({
		state,
		openProjectName: state.user.openProjectName
	})

	// Process project routes
	const projectRoutes = processRouteConfigs(projectRoutesConfig)

	// Get common routes
	const commonRoutes = getCommonRoutes()

	// Build routes based on authentication status
	if (isLogged) {
		// Logged in routes
		const routesContent = (
			<Routes>
				{commonRoutes}
				{getCoreRoutes()}
				{projectRoutes.map((route, index) => {
					const { path, element } = route
					return (
						<Route 
							key={path || `route-${index}`} 
							path={path} 
							element={element} 
						/>
					)
				})}
			</Routes>
		)
		
		// Wrap with Stripe integration hook
		return (
			<RoutesWithStripeIntegration isLogged={isLogged}>
				<Suspense fallback={<RouteChunkFallback />}>
					{routesContent}
				</Suspense>
			</RoutesWithStripeIntegration>
		)
	} else {
		// Not logged in routes
		return (
			<Suspense fallback={<RouteChunkFallback />}>
				<Routes>
					{commonRoutes}
					{getAuthRoutes(isLoggingIn)}
				</Routes>
			</Suspense>
		)
	}
}

/**
 * Create routes for a project (wrapper function for backward compatibility)
 * @param {object} config - Project route configuration
 * @param {boolean} isLogged - Is user logged in
 * @param {boolean} isLoggingIn - Is user currently logging in
 * @returns {JSX.Element} Routes component
 */
export const createProjectRoutes = (config, isLogged, isLoggingIn) => {
	return <ProjectRoutes config={config} isLogged={isLogged} isLoggingIn={isLoggingIn} />
}

/**
 * Routes From Configs Component
 * React component that generates routes from route configurations array
 * @param {object} props - Component props
 * @param {Array} props.routeConfigs - Array of route configurations
 * @param {boolean} props.isLogged - Is user logged in
 * @param {boolean} props.isLoggingIn - Is user currently logging in
 * @returns {JSX.Element} Routes component
 */
export const RoutesFromConfigs = ({ routeConfigs, isLogged, isLoggingIn }) => {
	const state = useSelector(state => state)

	// Get common routes
	const commonRoutes = getCommonRoutes()

	// Process route configs
	const processedRoutes = processRouteConfigs(routeConfigs)

	if (isLogged) {
		return (
			<Suspense fallback={<RouteChunkFallback />}>
				<Routes>
					{commonRoutes}
					{getCoreRoutes()}
					{processedRoutes.map((route, index) => {
						const { path, element } = route
						return (
							<Route 
								key={path || `route-${index}`} 
								path={path} 
								element={element} 
							/>
						)
					})}
				</Routes>
			</Suspense>
		)
	} else {
		return (
			<Suspense fallback={<RouteChunkFallback />}>
				<Routes>
					{commonRoutes}
					{getAuthRoutes(isLoggingIn)}
				</Routes>
			</Suspense>
		)
	}
}

/**
 * Create routes from route configurations array (wrapper function for backward compatibility)
 * @param {Array} routeConfigs - Array of route configurations
 * @param {boolean} isLogged - Is user logged in
 * @param {boolean} isLoggingIn - Is user currently logging in
 * @returns {JSX.Element} Routes component
 */
export const createRoutesFromConfigs = (routeConfigs, isLogged, isLoggingIn) => {
	return <RoutesFromConfigs routeConfigs={routeConfigs} isLogged={isLogged} isLoggingIn={isLoggingIn} />
}
